import { useState, useEffect } from "react";
import { AlertDialog, AlertDialogAction, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import BandDisagreementDialog from "./BandDisagreementDialog";
import { useBandingData } from "./banding/hooks/useBandingData";
import { useBandingActions } from "./banding/hooks/useBandingActions";
import { useSupervisorStatus } from "./banding/hooks/useSupervisorStatus";
import { useDialogManagement } from "./banding/hooks/useDialogManagement";
import PersonalGoalsContent from "./banding/PersonalGoalsContent";
import SupervisorTabContent from "./banding/SupervisorTabContent";
import { supabase } from "@/integrations/supabase/client";
import { usePerformanceYear } from "@/contexts/PerformanceYearContext";

const PerformanceBandingTab = () => {
  const [activeTab, setActiveTab] = useState("personal-goals");
  const [bandDisagreementStatus, setBandDisagreementStatus] = useState<string | null>(null);
  const { activeYear } = usePerformanceYear();

  const {
    isSupervisor,
    isLoading,
    supervisorName,
    userPerformanceBand,
    setUserPerformanceBand,
    userFullName
  } = useSupervisorStatus();

  // Move all hook calls before any conditional returns
  const {
    updatedTeamMembersData,
    isLoading: isBandingLoading,
    currentUser,
    bandBoundaries,
    bandPercentages,
    bellCurveRefreshTrigger,
    handleBoundariesChange,
    handlePercentageChange,
    handlePercentageCommit,
    resetBoundaries,
    getBandStatistics,
    setTeamMembersData,
    saveBandingToDatabase
  } = useBandingData();

  console.log('PerformanceBandingTab: Current user from useBandingData:', currentUser);
  console.log('PerformanceBandingTab: Is supervisor:', isSupervisor);
  console.log('PerformanceBandingTab: Loading states - supervisor:', isLoading, 'banding:', isBandingLoading);

  const {
    showSuccessDialog,
    setShowSuccessDialog,
    showErrorDialog,
    setShowErrorDialog,
    dialogMessage,
    isSavingAll,
    isReleasedBanding,
    handleSave,
    handleRowSave,
    handleRowEdit,
    handleCommentsChange
  } = useBandingActions(currentUser, updatedTeamMembersData, setTeamMembersData, saveBandingToDatabase);

  const {
    showDisagreementDialog,
    setShowDisagreementDialog,
    showBandDisagreementDialog,
    setShowBandDisagreementDialog,
    showAcceptDialog,
    setShowAcceptDialog,
    showDisagreeDialog,
    setShowDisagreeDialog,
    showErrorDialog: showDialogError,
    setShowErrorDialog: setShowDialogError,
    dialogMessage: dialogErrorMessage,
    handleAccept,
    handleDisagree,
    handleDisagreementSuccess,
    handleDisagreementError
  } = useDialogManagement(currentUser, userPerformanceBand, setUserPerformanceBand);

  // Fetch band disagreement status for team members - filter by activeYear
  useEffect(() => {
    if (!isSupervisor && currentUser) {
      const fetchBandDisagreementStatus = async () => {
        try {
          const { data, error } = await supabase
            .from('band_disagreements')
            .select('status')
            .eq('user_id', currentUser.id)
            .eq('year', activeYear)
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle();

          if (error) {
            console.error('Error fetching band disagreement status:', error);
            return;
          }

          setBandDisagreementStatus(data?.status || null);
        } catch (error) {
          console.error('Error fetching band disagreement status:', error);
        }
      };

      fetchBandDisagreementStatus();
    }
  }, [isSupervisor, currentUser, activeYear]);

  const bandStats = getBandStatistics();

  if (isLoading || isBandingLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-primary animate-pulse">Loading performance band data...</div>
      </div>
    );
  }

  if (!isSupervisor) {
    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-bold text-white">Performance Band Distribution</h2>
          {supervisorName && (
            <div className="flex items-center gap-2 text-white text-[11px]">
              <span>Supervisor:</span>
              <Badge variant="secondary" className="bg-blue-500/20 text-blue-400 border-blue-500/30">
                {supervisorName}
              </Badge>
            </div>
          )}
        </div>
        
        <div className="border border-purple-500/30 rounded-lg p-3">
          <p className="text-white text-[13px]">
            For team members: The final performance band is allocated by your direct supervisor. Kindly contact your supervisor if you have any questions.
          </p>
        </div>
        
        <PersonalGoalsContent
          userPerformanceBand={userPerformanceBand}
          onAccept={handleAccept}
          onDisagree={handleDisagree}
          bandDisagreementStatus={bandDisagreementStatus}
          supervisorName={supervisorName}
        />

        <BandDisagreementDialog
          isOpen={showBandDisagreementDialog}
          onClose={() => setShowBandDisagreementDialog(false)}
          userPerformanceBand={userPerformanceBand}
          currentUser={currentUser}
          supervisorName={supervisorName}
          employeeName={userFullName}
          onSuccess={handleDisagreementSuccess}
          onError={handleDisagreementError}
        />

        {/* Accept Dialog */}
        <AlertDialog open={showAcceptDialog} onOpenChange={setShowAcceptDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Success</AlertDialogTitle>
              <AlertDialogDescription>Final band acceptance saved</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogAction onClick={() => setShowAcceptDialog(false)}>
                OK
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Disagree Dialog */}
        <AlertDialog open={showDisagreeDialog} onOpenChange={setShowDisagreeDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Success</AlertDialogTitle>
              <AlertDialogDescription>Band disagreement submitted</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogAction onClick={() => setShowDisagreeDialog(false)}>
                OK
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Error Dialog */}
        <AlertDialog open={showDialogError} onOpenChange={setShowDialogError}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Error</AlertDialogTitle>
              <AlertDialogDescription>{dialogErrorMessage}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogAction onClick={() => setShowDialogError(false)}>
                OK
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-bold text-white">Performance Band Distribution</h2>
          {supervisorName && (
            <div className="flex items-center gap-2 text-white text-[11px]">
              <span>Supervisor:</span>
              <Badge variant="secondary" className="bg-blue-500/20 text-blue-400 border-blue-500/30">
                {supervisorName}
              </Badge>
            </div>
          )}
        </div>
        
        <SupervisorTabContent
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          updatedTeamMembersData={updatedTeamMembersData}
          bandStats={bandStats}
          bandPercentages={bandPercentages}
          handlePercentageChange={handlePercentageChange}
          handlePercentageCommit={handlePercentageCommit}
          resetBoundaries={resetBoundaries}
          currentUser={currentUser}
          bandBoundaries={bandBoundaries}
          handleBoundariesChange={handleBoundariesChange}
          bellCurveRefreshTrigger={bellCurveRefreshTrigger}
          isSavingAll={isSavingAll}
          handleSave={handleSave}
          handleRowSave={handleRowSave}
          handleRowEdit={handleRowEdit}
          handleCommentsChange={handleCommentsChange}
          isReleasedBanding={isReleasedBanding}
          userPerformanceBand={userPerformanceBand}
          onAccept={handleAccept}
          onDisagree={handleDisagree}
        />
        
        <BandDisagreementDialog
          isOpen={showBandDisagreementDialog}
          onClose={() => setShowBandDisagreementDialog(false)}
          userPerformanceBand={userPerformanceBand}
          currentUser={currentUser}
          supervisorName={supervisorName}
          employeeName={userFullName}
          onSuccess={handleDisagreementSuccess}
          onError={handleDisagreementError}
        />
      </div>

      {/* all alert dialogs */}
      <AlertDialog open={showSuccessDialog} onOpenChange={setShowSuccessDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Success</AlertDialogTitle>
            <AlertDialogDescription>{dialogMessage}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setShowSuccessDialog(false)}>
              OK
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showErrorDialog} onOpenChange={setShowErrorDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Error</AlertDialogTitle>
            <AlertDialogDescription>{dialogMessage}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setShowErrorDialog(false)}>
              OK
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showDisagreementDialog} onOpenChange={setShowDisagreementDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disagreement Submitted</AlertDialogTitle>
            <AlertDialogDescription>
              Your performance band disagreement has been submitted successfully. It will be reviewed by your supervisor.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setShowDisagreementDialog(false)}>
              OK
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showAcceptDialog} onOpenChange={setShowAcceptDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Success</AlertDialogTitle>
            <AlertDialogDescription>Final band acceptance saved</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setShowAcceptDialog(false)}>
              OK
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showDisagreeDialog} onOpenChange={setShowDisagreeDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Success</AlertDialogTitle>
            <AlertDialogDescription>Band disagreement submitted</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setShowDisagreeDialog(false)}>
              OK
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showDialogError} onOpenChange={setShowDialogError}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Error</AlertDialogTitle>
            <AlertDialogDescription>{dialogErrorMessage}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setShowDialogError(false)}>
              OK
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default PerformanceBandingTab;
